import React, {useContext, useEffect, useState} from 'react'
import {Box, Fab, IconButton, Tooltip, Typography} from "@material-ui/core";
import {ReactGrid} from 'components/helpers/reactGrid/reactGrid';
import {useSnackbar} from "notistack";
import {useConfirm} from 'material-ui-confirm';
import SubHeader from "components/helpers/subHeader/subHeader";
import {MainContext} from "contexts/main.context";
import {useEquivalenciasService} from "services/hooks";
import {CodigosEquivalentes} from "components/helpers/equivalencias/codigoEquivalente";
import {ArticuloDetailsDialog} from "components/helpers/articulos/articulo.details.dialog";
import {EquivalenciasEditDialog} from "components/stock/equivalencias/equivalencias.edit.dialog";
import {AddRounded, DeleteRounded, EditRounded} from "@material-ui/icons";

/**
 * Equivalencias de artículos: consulta y carga.
 *
 * Las columnas se arman con el maestro de marcas, así que sumar un proveedor nuevo
 * no requiere tocar esta pantalla. Los códigos que existen en el catálogo se muestran
 * como link y abren el detalle del artículo.
 *
 * La carga (alta, edición y baja) se habilita según el rol. El backend igual rechaza
 * las escrituras de quien no corresponda, así que esto es solo la afordancia visual.
 */
export const Equivalencias = () => {
    const [marcas, setMarcas] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [grupoEnEdicion, setGrupoEnEdicion] = useState(null);
    const [recargas, setRecargas] = useState(0);
    // Artículo cuyo detalle se está mostrando al clickear un código del catálogo.
    const [articuloVisible, setArticuloVisible] = useState(null);

    const EquivalenciasService = useEquivalenciasService();
    const {enqueueSnackbar} = useSnackbar();
    const confirm = useConfirm();
    const context = useContext(MainContext);

    const puedeEditar = ["A", "O"].includes(context.loggedUser.role);

    useEffect(() => {
        EquivalenciasService.getMarcas(
            response => setMarcas(response.data.result),
            error => enqueueSnackbar(error, {variant: 'error'})
        );
    }, []);

    // Traigo todos los grupos una sola vez y la búsqueda la resuelve el buscador del
    // grid en memoria (ver los valores de texto que se arman en `rows`).
    // La recarga se dispara por estado y no llamando a una función: ReactGrid congela
    // los renderers de celda en su constructor, y un closure viejo llamaría a una
    // versión desactualizada. `setRecargas` es estable, así que funciona igual.
    useEffect(() => {
        EquivalenciasService.getAll(
            null,
            response => setGrupos(response.data.result),
            error => enqueueSnackbar(error, {variant: 'error'})
        );
    }, [recargas]);

    const recargar = () => setRecargas(recargas => recargas + 1);

    const onNuevo = () => {
        setGrupoEnEdicion(null);
        setEditDialogOpen(true);
    };

    const onEditar = grupo => {
        setGrupoEnEdicion(grupo);
        setEditDialogOpen(true);
    };

    const onConfirmEdit = (codigos, observaciones) => {
        const onSuccess = mensaje => {
            setEditDialogOpen(false);
            enqueueSnackbar(mensaje, {variant: 'success'});
            recargar();
        };

        if (grupoEnEdicion)
            EquivalenciasService.edit(
                grupoEnEdicion.id,
                codigos,
                observaciones,
                () => onSuccess('La equivalencia se editó correctamente'),
                error => enqueueSnackbar(error, {variant: 'error'})
            );
        else
            EquivalenciasService.create(
                codigos,
                observaciones,
                () => onSuccess('La equivalencia se creó correctamente'),
                error => enqueueSnackbar(error, {variant: 'error'})
            );
    };

    const onEliminar = grupo => {
        confirm({
            title: '¿Eliminar la equivalencia?',
            content: (
                <Typography variant={"body2"}>
                    Se eliminarán los {grupo.codigos.length} códigos del grupo.
                    Solo se eliminan las equivalencias, los artículos reales no se modifican.
                </Typography>
            ),
            confirmationText: 'Eliminar',
            cancellationText: 'Cancelar'
        }).then(() => {
            EquivalenciasService.delete(
                grupo.id,
                () => {
                    enqueueSnackbar('La equivalencia se eliminó correctamente', {variant: 'success'});
                    recargar();
                },
                error => enqueueSnackbar(error, {variant: 'error'})
            );
        }).catch(() => {
            // Cancelado por el usuario.
        });
    };

    // Una columna por marca + observaciones (+ acciones si puede editar).
    // ReactGrid solo aplica el renderer `component` a las columnas que se llaman
    // "custom...", así que las nombro por índice y guardo la marca en el extension.
    const columns = [
        ...marcas.map((marca, index) => ({name: `custom${index}`, title: marca.description})),
        {name: 'observaciones', title: 'Observaciones'}
    ];

    const columnExtensions = [
        ...marcas.map((marca, index) => ({
            columnName: `custom${index}`,
            width: 150,
            sortingEnabled: false,
            component: ({row}) => (
                <CodigosEquivalentes
                    codigos={row.codigosPorMarca[marca.id]}
                    onNavigate={setArticuloVisible}
                />
            )
        })),
        {columnName: 'observaciones', width: 140}
    ];

    if (puedeEditar) {
        columns.push({name: `custom${marcas.length}`, title: ' '});
        columnExtensions.push({
            columnName: `custom${marcas.length}`,
            width: 110,
            align: 'center',
            sortingEnabled: false,
            component: ({row}) => (
                <>
                    <Tooltip title={'Editar'}>
                        <IconButton size={"small"} onClick={() => onEditar(row.grupo)}>
                            <EditRounded fontSize={"small"}/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={'Eliminar'}>
                        <IconButton size={"small"} onClick={() => onEliminar(row.grupo)}>
                            <DeleteRounded fontSize={"small"}/>
                        </IconButton>
                    </Tooltip>
                </>
            )
        });
    }

    // Cada grupo es una fila: los códigos se reparten en la columna de su marca.
    // Además de `codigosPorMarca` (que usa el renderer), cada columna necesita su
    // valor como texto plano en `custom{i}`: el buscador del grid filtra por
    // row[column.name], así que sin esto no encontraría nada en estas columnas.
    const rows = grupos.map(grupo => {
        const codigosPorMarca = {};
        const row = {id: grupo.id, observaciones: grupo.observaciones, grupo};

        marcas.forEach((marca, index) => {
            const codigos = grupo.codigos.filter(codigo => codigo.marca === marca.id);
            codigosPorMarca[marca.id] = codigos;
            row[`custom${index}`] = codigos.map(codigo => codigo.codigo).join(' ');
        });

        return {...row, codigosPorMarca};
    });

    return (
        <Box className='niquel-equivalencias'>
            <SubHeader
                title={'Equivalencias'}
                subtitle={'Equivalencias de Artículos'}
            />
            <Box className='container-xl mt-3 mb-3'>
                {/* Recién monto la grilla cuando están las marcas: ReactGrid congela
                    columnExtensions en su constructor, así que las columnas tienen que
                    estar completas desde el primer render. */}
                {marcas.length > 0 && (
                    <ReactGrid
                        columns={columns}
                        columnExtensions={columnExtensions}
                        rows={rows}
                        height={-200}
                        title={`Equivalencias (${rows.length})`}
                        enableFiltering={false}
                        enableSorting={false}
                        enableSearch={true}
                        enableSelection={false}
                    />
                )}
                {puedeEditar && (
                    <Tooltip title={'Nueva equivalencia'}>
                        <Box className={'niquel-fab-btn'}>
                            <Fab color={"primary"} onClick={onNuevo}>
                                <AddRounded/>
                            </Fab>
                        </Box>
                    </Tooltip>
                )}
            </Box>
            {puedeEditar && (
                <EquivalenciasEditDialog
                    open={editDialogOpen}
                    grupo={grupoEnEdicion}
                    marcas={marcas}
                    onConfirm={onConfirmEdit}
                    onCancel={() => setEditDialogOpen(false)}
                />
            )}
            {articuloVisible && (
                <ArticuloDetailsDialog
                    open={true}
                    id={articuloVisible}
                    onCancel={() => setArticuloVisible(null)}
                />
            )}
        </Box>
    )
};
